import { useEffect, useMemo, useState } from "react";
import { Col, Container } from "reactstrap";
import { ReactComponent as Trennlinie } from "../img/line-headline.svg";
import { useCollectionDataOnce } from "react-firebase-hooks/firestore";
import { firestore, storage } from "../firebase";
import { StimmexText } from "../Types";
import FestivalLogosBoard, {
  FestivalLogosBoardVariant,
} from "../component/FestivalLogosBoard";
import cooperationArtwork from "../img/festival/other/coopimg.jpeg";

const FESTIVAL_CONTENT_SLIDER_AUTOPLAY_INTERVAL_MS = 7000;

const FestivalContentGallerySlider: React.FC<{
  imageGallery?: string[];
  title: string;
}> = ({ imageGallery, title }) => {
  const [galleryUrls, setGalleryUrls] = useState<string[]>([]);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);

  const galleryPaths = useMemo(
    () =>
      (imageGallery || []).filter(
        (path): path is string =>
          typeof path === "string" && path.trim().length > 0
      ),
    [imageGallery]
  );

  useEffect(() => {
    let isActive = true;

    if (galleryPaths.length === 0) {
      setGalleryUrls([]);
      setActiveGalleryIndex(0);
      return () => {
        isActive = false;
      };
    }

    const loadGalleryUrls = async () => {
      try {
        const urls = await Promise.all(
          galleryPaths.map(async (path) => {
            if (/^https?:\/\//i.test(path)) {
              return path;
            }

            if (path.startsWith("gs://")) {
              return storage.refFromURL(path).getDownloadURL();
            }

            return storage.ref(path).getDownloadURL();
          })
        );

        if (isActive) {
          setGalleryUrls(urls);
          setActiveGalleryIndex(0);
        }
      } catch (error) {
        console.error("Failed to load content image gallery", error);
        if (isActive) {
          setGalleryUrls([]);
          setActiveGalleryIndex(0);
        }
      }
    };

    loadGalleryUrls();

    return () => {
      isActive = false;
    };
  }, [galleryPaths]);

  const hasGallery = galleryUrls.length > 0;

  useEffect(() => {
    if (!hasGallery || galleryUrls.length <= 1) {
      return;
    }

    const autoplayInterval = window.setInterval(() => {
      setActiveGalleryIndex(
        (previousIndex) => (previousIndex + 1) % galleryUrls.length
      );
    }, FESTIVAL_CONTENT_SLIDER_AUTOPLAY_INTERVAL_MS);

    return () => {
      window.clearInterval(autoplayInterval);
    };
  }, [hasGallery, galleryUrls.length]);

  useEffect(() => {
    if (galleryUrls.length > 0 && activeGalleryIndex >= galleryUrls.length) {
      setActiveGalleryIndex(0);
    }
  }, [activeGalleryIndex, galleryUrls.length]);

  const showPreviousGalleryImage = () => {
    if (galleryUrls.length <= 1) {
      return;
    }

    setActiveGalleryIndex((previousIndex) =>
      previousIndex === 0 ? galleryUrls.length - 1 : previousIndex - 1
    );
  };

  const showNextGalleryImage = () => {
    if (galleryUrls.length <= 1) {
      return;
    }

    setActiveGalleryIndex(
      (previousIndex) => (previousIndex + 1) % galleryUrls.length
    );
  };

  if (!hasGallery) {
    return null;
  }

  return (
    <div className="festival-event-slider">
      <div className="festival-event-slider-band">
        <div
          className="festival-event-slider-track"
          style={{
            transform: `translateX(calc(${activeGalleryIndex} * -1 * (var(--festival-event-slide-width) + var(--festival-event-slide-gap))))`,
          }}
        >
          {galleryUrls.map((url, index) => (
            <div key={`${url}-${index}`} className="festival-event-slide">
              <img
                className="image-responsive"
                src={url}
                alt={`${title} ${index + 1}`}
              ></img>
            </div>
          ))}
        </div>
        {galleryUrls.length > 1 && (
          <>
            <button
              type="button"
              className="festival-event-arrow festival-event-arrow-left"
              aria-label="Vorheriges Bild"
              onClick={showPreviousGalleryImage}
            >
              ‹
            </button>
            <button
              type="button"
              className="festival-event-arrow festival-event-arrow-right"
              aria-label="Naechstes Bild"
              onClick={showNextGalleryImage}
            >
              ›
            </button>
          </>
        )}
      </div>
      {galleryUrls.length > 1 && (
        <div
          className="festival-event-indicators"
          role="tablist"
          aria-label="Bildauswahl"
        >
          {galleryUrls.map((_, index) => (
            <button
              key={`indicator-${index}`}
              type="button"
              className={`festival-event-indicator ${
                index === activeGalleryIndex ? "active" : ""
              }`}
              aria-label={`Bild ${index + 1} anzeigen`}
              aria-current={index === activeGalleryIndex ? "true" : undefined}
              onClick={() => setActiveGalleryIndex(index)}
            ></button>
          ))}
        </div>
      )}
    </div>
  );
};

type FestivalContent2026BaseProps = {
  setName: (name: string) => void;
  title: string;
  section: string;
  placeholder: string;
  logosBoardVariant?: FestivalLogosBoardVariant;
};

const FestivalContent2026Base: React.FC<FestivalContent2026BaseProps> = ({
  setName,
  title,
  section,
  placeholder,
  logosBoardVariant,
}) => {
  setName(title);
  const showCooperationsFeature = section === "festival-cooperations2026";

  const [subsections] = useCollectionDataOnce<StimmexText>(
    firestore
      .collection(`/texte`)
      .where("section", "==", section)
      .orderBy("index", "asc")
  );

  return (
    <Container>
      <Col sm="12">
        <h1 className="subsection">
          <Trennlinie className="svg-icon svg-baseline subsection-separator" />{" "}
          {title}
        </h1>
      </Col>

      <Col sm="12">
        {subsections && subsections.length > 0 ? (
          subsections.map((s) => (
            <div key={s.path || s.title}>
              <FestivalContentGallerySlider
                imageGallery={s.imageGallery}
                title={s.title || title}
              />
              <div dangerouslySetInnerHTML={{ __html: s.content }} />
            </div>
          ))
        ) : (
          <p>{placeholder}</p>
        )}
      </Col>

      {logosBoardVariant && (
        <Col sm="12" className="festival-cooperations-logos-wrapper">
          <FestivalLogosBoard variant={logosBoardVariant} />
        </Col>
      )}

      {showCooperationsFeature && (
        <Col sm="12" className="festival-cooperations-feature">
          <p className="festival-cooperations-feature-intro">
            Im F Pavillon zeigt das Festival die Produktion „Große Brüste,
            Fratze, Schwanz“ in Kooperation mit der Ausstellung „Mein
            Wesentliches“.
          </p>

          <img
            className="festival-cooperations-feature-image"
            src={cooperationArtwork}
            alt="Fresco von Sabine Kisvari auf Seidelbastpapier"
          />
          <p className="festival-cooperations-feature-credit">
            Sabine Kisvari/Fresco auf Seidelbastpapier/2025
          </p>

          <p>
            „Mein Wesentliches“ Ein leiser Ruf nach innen - und ein sichtbares
            Echo nach außen. Dies ist eine Ausstellung wie ein geöffnetes Herz.
            Ein Raum, in dem das Unsagbare Form annimmt und das Verborgene leise
            zu sprechen beginnt. „Mein Wesentliches“ versammelt das, was lange
            im Inneren gewachsen ist - Sehnsucht, Fragen, Erinnerungen und
            Hoffnung. Es sind Spuren eines persönlichen Weges, Fragmente eines
            stillen Dialogs mit sich selbst. Jede Arbeit ist ein Versuch, das
            Flüchtige festzuhalten, das Zarte sichtbar zu machen und dem
            Innersten eine Gestalt zu geben. Es ist eine Einladung,
            innezuhalten, sich zu begegnen - und vielleicht im Anderen auch ein
            Stück des eigenen Wesentlichen zu erkennen.
          </p>

          <p className="festival-cooperations-feature-artists">
            Gruppenausstellung Künstler*innen: Antje TieBec, Sabine Kisvari,
            Linda Bäppler, Laura-Solmaz Litschel, Shahla Khosravi, Barbara
            Maahs, Chefxoch, normanexSE, Bernd Muss, Tattooartwaldi, Arash
            Zanjani, Hamsa Jallow, Sajad Goodarzi und Omeed Khadem Saba
          </p>
        </Col>
      )}

      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
    </Container>
  );
};

export const FestivalAbout2026: React.FC<{ setName: (name: string) => void }> = ({
  setName,
}) => (
  <FestivalContent2026Base
    setName={setName}
    title="About"
    section="festival-about2026"
    placeholder="Inhalt folgt in Kürze."
  />
);

export const FestivalArtists2026: React.FC<{
  setName: (name: string) => void;
}> = ({ setName }) => (
  <FestivalContent2026Base
    setName={setName}
    title="Artists"
    section="festival-artists2026"
    placeholder="Künstler*innen-Infos folgen in Kürze."
  />
);

export const FestivalCooperations2026: React.FC<{
  setName: (name: string) => void;
}> = ({ setName }) => (
  <FestivalContent2026Base
    setName={setName}
    title="Cooperations"
    section="festival-cooperations2026"
    placeholder="Kooperations-Inhalte folgen in Kürze."
    logosBoardVariant="cooperations"
  />
);

export const FestivalVenues2026: React.FC<{ setName: (name: string) => void }> = ({
  setName,
}) => (
  <FestivalContent2026Base
    setName={setName}
    title="Venues"
    section="festival-venues2026"
    placeholder="Spielorte folgen in Kürze."
  />
);
